import { Controller } from "@hotwired/stimulus"
import { introAlreadyPlayed } from "../helpers/intro_once"
import { rollDigits } from "../helpers/digit_roll"

// Key for the once-per-device auto-reveal of the bank sync strip. Same gate
// the actions mascots use (intro_once_controller).
const STRIP_INTRO_KEY = "bank-sync-strip"

// The budget whose card is showing, so the header colour picker
// (header_gradient_preview_controller) previews the same card. localStorage
// is shared with the Settings sheet, as the card skins are.
export const ACTIVE_BUDGET_KEY = "dashboard-active-budget"

// The budget card stage: one card at a time, in place. Every budget's card
// and content panel is server-rendered; this shows one pair and hides the
// rest, and swaps them with the pace strip's handover
// (spending_pace_controller) minus its slide: the card's surface stays put
// (every card wears the same shell), what is on it fades up from a soft
// blur, the figures roll their digits in from above and the bar regrows. The
// panel under the card arrives the same way. Nothing sits beside the card and
// nothing travels — the card for the fifth budget arrives exactly as the card
// for the second does.
//
// A sideways swipe on the stage changes the budget, read from pointer events
// with the pace strip's own rule (slop, commit, click guard). The stage sets
// no touch-action: a non-passive touchmove handler decides each gesture
// instead — vertical ones go to the page untouched, horizontal ones are
// cancelled move by move so the page never pans under them (with pan-y,
// WebKit would start the pan before asking). The dots under the card are the
// indicator: they show which budget is on stage and a tap on one does the
// same as a swipe.
export default class extends Controller {
  static targets = ["stage", "card", "dot", "dots", "contentPanel"]
  static values = {
    activeIndex: { type: Number, default: 0 },
    saveUrls: Object,
    newUrls: Object,
    budgetUrls: Object
  }

  // Movement under SLOP is a tap. Past it the gesture commits to horizontal or
  // hands over to the page. COMMIT is how far the finger travels before the
  // budget changes. Same numbers as the pace strip.
  static SWIPE_SLOP_PX = 6
  static SWIPE_COMMIT_PX = 24
  // The click that trails a swipe would land on whatever the finger lifted
  // over — the card's menu, say. Ignore it.
  static CLICK_GUARD_MS = 300
  // The handover's length (budget-arrive in application.css). The next
  // budget page is fetched only once it is over.
  static HANDOVER_MS = 320
  // The glide's first half: how long the outgoing budget blurs away before
  // the next one is shown (budget-glide-out in index.html.erb).
  static GLIDE_OUT_MS = 140

  // The bank sync strip announces itself once per device — the first time a
  // card with a linked bank settles — holds long enough to read the bank and
  // reach Sync, then retracts. It never auto-opens again: from then on the
  // chevron on the card's bottom edge is the only way to bring it back.
  static STRIP_REVEAL_DELAY_MS = 1000

  // The dots are out in full when the dashboard arrives, then drop away —
  // "your spending pace" does exactly this with its period tabs, and this is
  // its clock: spending_pace_controller's SETTLE_MS (1120, the handover) plus
  // SETTLE_HOLD_MS (500, a beat so the last thing to land is not also the cue
  // to leave). The fade itself is that section's 420ms, in the CSS.
  //
  // They come back for a swipe, because mid-gesture they are the only thing
  // saying how many budgets there are and where this one sits among them.
  static DOTS_HIDE_DELAY_MS = 1620
  static STRIP_VISIBLE_MS = 3000

  connect() {
    this._reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches

    this._onPointerDown = this._pointerDown.bind(this)
    this._onPointerMove = this._pointerMove.bind(this)
    this._onPointerUp = this._pointerUp.bind(this)
    this._onTouchMove = this._touchMove.bind(this)
    this._onClick = this._click.bind(this)
    const stage = this.stageTarget
    stage.addEventListener("pointerdown", this._onPointerDown)
    stage.addEventListener("pointermove", this._onPointerMove)
    stage.addEventListener("pointerup", this._onPointerUp)
    stage.addEventListener("pointercancel", this._onPointerUp)
    // Not passive on purpose: a gesture that has committed to horizontal
    // cancels its own touchmoves, so the page never pans under it.
    stage.addEventListener("touchmove", this._onTouchMove, { passive: false })
    stage.addEventListener("click", this._onClick, true)

    // Show the active pair at once, no animation (Turbo cache restore and
    // body replacements arrive with whatever was visible last).
    this._handover(false)
    this._revealStrip(this.constructor.STRIP_REVEAL_DELAY_MS)
    this._showDots()
    this._hideDotsSoon()
  }

  disconnect() {
    const stage = this.stageTarget
    stage.removeEventListener("pointerdown", this._onPointerDown)
    stage.removeEventListener("pointermove", this._onPointerMove)
    stage.removeEventListener("pointerup", this._onPointerUp)
    stage.removeEventListener("pointercancel", this._onPointerUp)
    stage.removeEventListener("touchmove", this._onTouchMove)
    stage.removeEventListener("click", this._onClick, true)
    clearTimeout(this._stripRevealTimer)
    clearTimeout(this._stripHideTimer)
    clearTimeout(this._dotsTimer)
    clearTimeout(this._prefetchTimer)
    clearTimeout(this._glideTimer)
  }

  // ── Swipe ─────────────────────────────────────────────────────────

  _pointerDown(event) {
    // The chevron and the strip itself are controls, not a grip on the card.
    if (event.target.closest(".bank-sync-toggle, .bank-sync-strip")) return

    this._dragging = true
    // Touching the card is enough — the strip retracts before anything moves.
    this._hideStrip()

    this._startX = event.clientX
    this._startY = event.clientY
    this._committed = false
    this._swiped = false
  }

  _pointerMove(event) {
    if (this._startX == null || this._swiped) return
    const dx = event.clientX - this._startX
    const dy = event.clientY - this._startY

    if (!this._committed) {
      const { SWIPE_SLOP_PX } = this.constructor
      if (Math.abs(dx) < SWIPE_SLOP_PX && Math.abs(dy) < SWIPE_SLOP_PX) return
      // Mostly vertical: the page scrolls, this gesture is over.
      if (Math.abs(dy) > Math.abs(dx)) { this._startX = null; return }
      this._committed = true
      this._showDots()
    }
    if (Math.abs(dx) < this.constructor.SWIPE_COMMIT_PX) return

    // One budget per gesture, however far the finger goes on.
    this._swiped = true
    this._swipedAt = Date.now()
    let step = dx < 0 ? 1 : -1
    // The glide follows the finger, not the reading order.
    this._glideDir = step
    if (this._isRtl()) step = -step
    this._goTo(this.activeIndexValue + step, true)
  }

  // pan-y lets the page scroll over the card, and a finger is never perfectly
  // level: once the gesture is ours, its vertical remainder must not move the
  // page (the band and the pills above were seen lifting and settling back
  // under a swipe). The commit rule runs here as well as in _pointerMove,
  // because this touchmove is dispatched before its pointermove: the move
  // that commits is the first one cancelled, so the page never gets a
  // touchmove it may pan on.
  _touchMove(event) {
    if (this._startX == null || event.touches.length !== 1) return
    if (!this._committed) {
      const touch = event.touches[0]
      const dx = touch.clientX - this._startX
      const dy = touch.clientY - this._startY
      const { SWIPE_SLOP_PX } = this.constructor
      if (Math.abs(dx) < SWIPE_SLOP_PX && Math.abs(dy) < SWIPE_SLOP_PX) return
      if (Math.abs(dy) > Math.abs(dx)) { this._startX = null; return }
      this._committed = true
      this._showDots()
    }
    if (event.cancelable) event.preventDefault()
  }

  _pointerUp() {
    this._startX = null
    this._committed = false
    if (!this._dragging) return
    this._dragging = false
    // Covers the tap that never moved anything; a change schedules its own.
    this._revealStrip(this.constructor.STRIP_REVEAL_DELAY_MS)
    this._hideDotsSoon()
  }

  _click(event) {
    if (this._swipedAt && Date.now() - this._swipedAt < this.constructor.CLICK_GUARD_MS) {
      event.preventDefault()
      event.stopImmediatePropagation()
    }
  }

  // ── Dots ──────────────────────────────────────────────────────────
  // Out on arrival and for the length of a swipe, settled away after it
  // lands. Tapping a dot keeps them up, because that tap settles a card and
  // schedules a fresh hide of its own.
  //
  // .is-settled is the hiding class, not the showing one, so the markup the
  // server sends is the visible state: no flash of hidden dots on first paint
  // or on a Turbo restore, and they stay put if the controller never boots.
  _showDots() {
    clearTimeout(this._dotsTimer)
    this.dotsTargets.forEach((band) => band.classList.remove("is-settled"))
  }

  _hideDotsSoon(delay = this.constructor.DOTS_HIDE_DELAY_MS) {
    clearTimeout(this._dotsTimer)
    this._dotsTimer = setTimeout(() => {
      if (this._dragging) return
      this.dotsTargets.forEach((band) => band.classList.add("is-settled"))
    }, delay)
  }

  _isRtl() {
    return document.dir === "rtl" || document.documentElement.dir === "rtl"
  }

  // ── Budget change ─────────────────────────────────────────────────

  // Makes this budget the active one; activeIndexValueChanged does the rest.
  _goTo(index, animated) {
    if (index < 0 || index >= this.cardTargets.length) return
    if (index === this.activeIndexValue) return
    this._animated = animated
    this.activeIndexValue = index
  }

  // A dot under the card was tapped.
  goToCard(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10)
    // As if the finger had swiped toward that dot.
    const forward = index > this.activeIndexValue ? 1 : -1
    this._glideDir = this._isRtl() ? -forward : forward
    this._goTo(index, true)
  }

  // Web dashboard: hover arrows and the arrow keys step through the budgets,
  // wrapping at either end. Reading order follows the page direction.
  prev() {
    this._step(-1)
  }

  next() {
    this._step(1)
  }

  key(event) {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return
    event.preventDefault()
    const forward = (event.key === "ArrowRight") !== this._isRtl()
    this._step(forward ? 1 : -1)
  }

  _step(delta) {
    const count = this.cardTargets.length
    if (count < 2) return
    this._glideDir = this._isRtl() ? -delta : delta
    this._goTo((this.activeIndexValue + delta + count) % count, true)
  }

  activeIndexValueChanged() {
    this._updateDots()
    this._applyActiveIndex()
  }

  _rememberActiveBudget() {
    const id = this.cardTargets[this.activeIndexValue]?.dataset.budgetSkinId
    if (!id) return
    try { localStorage.setItem(ACTIVE_BUDGET_KEY, id) } catch { /* storage blocked: preview shows the first card */ }
  }

  // The active dot is a 20px bar, the rest are 6px circles. Same class swap
  // the dots were painted with before they were replaced by the pills.
  _updateDots() {
    this.dotTargets.forEach((dot, index) => {
      const target = dot.querySelector("span") || dot
      if (index === this.activeIndexValue) {
        target.classList.remove("bg-base-content/25", "w-1.5")
        target.classList.add("bg-blue-600", "w-5")
      } else {
        target.classList.remove("bg-blue-600", "w-5")
        target.classList.add("bg-base-content/25", "w-1.5")
      }
    })
  }


  _applyActiveIndex() {
    // The first pass (Stimulus fires this on initialize, before connect)
    // is not animated: the pair is shown, nothing moves.
    this._handover(this._animated === true)
    this._animated = false
    // Every card that settles gets its own reveal.
    this._hideStrip()
    this._revealStrip(this.constructor.STRIP_REVEAL_DELAY_MS)
    this._hideDotsSoon()
    this._updateSaveUrl()
    this._rememberActiveBudget()
    this._updateNewUrl()
    this._updateBrowserUrl()
    // Once the handover is over: Turbo's preloader parses a whole document on
    // the main thread, which is time the animation needs.
    clearTimeout(this._prefetchTimer)
    const { GLIDE_OUT_MS, HANDOVER_MS } = this.constructor
    this._prefetchTimer = setTimeout(() => this._prefetchActive(), GLIDE_OUT_MS + HANDOVER_MS)
  }

  // ── Handover ──────────────────────────────────────────────────────

  // The pace strip's body handover, applied to the card and its content
  // panel together: the pair that was showing goes at once, the new pair
  // arrives in place (.budget-arrive).
  _handover(animated) {
    // The swap does not animate any more. The card's children arrived on
    // budget-arrive, its figures rolled their digits, and a sheen swept the
    // card, all at once, every time the budget changed. Going back and forth
    // between budgets a few times turned that into a hard flicker across the
    // whole dashboard on device.
    //
    // It is one constant rather than edits at the call sites so there is a
    // single place to turn it back on, and `animated` still arrives from every
    // caller carrying its intent. A budget change now glides instead (_glide).
    const animate = false

    if (animated && !this._reducedMotion) {
      this._glide()
      return
    }
    clearTimeout(this._glideTimer)
    this._gliding = false
    this._showCards(animated, { arrive: animate, glide: false })
    this._showPanels()
  }

  // The card only: what is on it blurs and drifts with the finger
  // (budget-glide-out), then the next budget's sharpens in from the other
  // side (budget-glide-in). The content panel under it swaps at once, as it
  // always has.
  // A change that lands mid-glide skips the rest of the way out and shows
  // the newest budget at once, so quick swipes never queue up.
  _glide() {
    clearTimeout(this._glideTimer)
    this.element.style.setProperty("--glide-dir", this._glideDir || 1)

    this._showPanels()

    if (this._gliding) {
      this._settleGlide()
      return
    }

    this._gliding = true
    const outgoing = this.cardTargets.filter((el) => !el.classList.contains("hidden"))
    outgoing.forEach((el) => {
      el.classList.remove("budget-glide-in")
      el.classList.add("budget-glide-out")
    })
    this._glideTimer = setTimeout(() => this._settleGlide(), this.constructor.GLIDE_OUT_MS)
  }

  _settleGlide() {
    this._gliding = false
    this._showCards(true, { arrive: false, glide: true })
  }

  _showCards(animated, { arrive, glide }) {
    const show = (el, on) => {
      const wasShown = !el.classList.contains("hidden")
      el.classList.remove("budget-arrive", "budget-sheen", "budget-glide-out", "budget-glide-in")
      if (!on) {
        el.classList.add("hidden")
        return
      }
      // An element that never left the screen (swiped back mid-glide) needs
      // a reflow for its glide-in to start over.
      if (glide && wasShown) void el.offsetWidth
      // The class goes on before the element is shown, so the first frame it
      // renders is already the animation's first keyframe.
      if (arrive) el.classList.add("budget-arrive")
      if (glide) el.classList.add("budget-glide-in")
      el.classList.remove("hidden")
    }

    this.cardTargets.forEach((card, index) => {
      const on = index === this.activeIndexValue
      show(card, on)
      // .is-active is what starts the bar's fill (.progress-animated).
      card.classList.toggle("is-active", on)
      // The figures still roll on a budget change — the pace strip's digit
      // handover. `arrive` keeps the card's arrival and the sheen off; the
      // roll is one transition per digit column and never repainted the
      // dashboard.
      if (on && animated && !this._reducedMotion) this._roll(card)
      if (on && arrive) this._sheen(card)
    })
  }

  // A panel names its budget with data-panel-index when a budget owns more
  // than one (the web dashboard's two columns); otherwise its position does.
  _showPanels() {
    this.contentPanelTargets.forEach((panel, index) => {
      const owner = panel.dataset.panelIndex === undefined ? index : Number(panel.dataset.panelIndex)
      panel.classList.toggle("hidden", owner !== this.activeIndexValue)
    })
  }

  // The pace strip's sheen (spending_pace_controller#_showBody), on the card:
  // re-adding the class after a reflow restarts the sweep every handover.
  _sheen(card) {
    card.classList.remove("budget-sheen")
    void card.offsetWidth
    card.classList.add("budget-sheen")
  }

  // The card's figures (counter_controller renders them) drop their digits in
  // from above, right-most first, as the pace amount does; once settled the
  // plain figure is put back (digit_roll).
  _roll(card) {
    card.querySelectorAll("[data-controller~='counter']").forEach((el) => rollDigits(el, true, { restore: true }))
  }

  // ── Bank sync strip ───────────────────────────────────────────────

  // toggleStrip is the chevron on the card's bottom edge. Opening by hand is
  // sticky — no auto-hide timer — because a deliberate tap should not be
  // undone a few seconds later. Changing budget still closes it.
  toggleStrip(event) {
    const card = event.currentTarget.closest(".budget-carousel-card")
    if (!card) return

    if (card.classList.contains("strip-open")) {
      this._hideStrip()
    } else {
      this._revealStrip(0, false)
    }
  }

  _revealStrip(delay = 0, autoHide = true) {
    clearTimeout(this._stripRevealTimer)
    clearTimeout(this._stripHideTimer)

    // Hand still on the card: a pause mid-gesture is not the card settling,
    // and revealing there is what made the strip flicker up and down during
    // a slow swipe. Release schedules the reveal instead.
    if (autoHide && this._dragging) return

    const open = () => {
      const card = this.cardTargets[this.activeIndexValue]
      if (!card) return

      // The auto-reveal is a one-time introduction, so it is spent the moment
      // it actually opens — not when it is scheduled, since a swipe can cancel
      // the timer before it ever fires. The chevron (autoHide false) is never
      // gated: a deliberate tap always opens the strip.
      if (autoHide) {
        if (this._autoRevealSpent) return
        this._autoRevealSpent = true
        if (introAlreadyPlayed(STRIP_INTRO_KEY)) return
      }

      card.classList.add("strip-open")
      this._stripOpen = true
      if (autoHide) {
        this._stripHideTimer = setTimeout(() => this._hideStrip(), this.constructor.STRIP_VISIBLE_MS)
      }
    }

    if (delay > 0) {
      this._stripRevealTimer = setTimeout(open, delay)
    } else {
      open()
    }
  }

  _hideStrip() {
    clearTimeout(this._stripRevealTimer)
    clearTimeout(this._stripHideTimer)
    if (!this._stripOpen) return
    this._stripOpen = false
    this.cardTargets.forEach((card) => card.classList.remove("strip-open"))
  }

  // ── URLs ──────────────────────────────────────────────────────────

  _prefetchActive() {
    const key = String(this.activeIndexValue)
    const budgetUrl = this.budgetUrlsValue[key]
    // Skip the budget URL that matches the page we actually rendered.
    if (budgetUrl && budgetUrl !== this._initialBudgetUrl()) this._preloadUrl(budgetUrl)
  }

  _preloadUrl(url) {
    this._prefetchedUrls ||= new Set()
    if (this._prefetchedUrls.has(url)) return
    this._prefetchedUrls.add(url)

    const preloader = window.Turbo?.session?.preloader
    if (preloader && typeof preloader.preloadURL === "function") {
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("data-turbo-preload", "")
      preloader.preloadURL(link).catch(() => { this._prefetchedUrls.delete(url) })
      return
    }

    fetch(url, {
      method: "GET",
      credentials: "same-origin",
      headers: {
        "Accept":      "text/html",
        "Sec-Purpose": "prefetch",
        "Purpose":     "prefetch",
        "X-Prefetch":  "budget-carousel"
      }
    }).catch(() => { this._prefetchedUrls.delete(url) })
  }

  _initialBudgetUrl() {
    // Memoized: the budget URL that matches the page we first rendered.
    // Captured lazily because activeIndexValueChanged may fire before connect().
    if (this._initialBudgetUrlCached === undefined) {
      const url = this.budgetUrlsValue[String(this.activeIndexValue)] || null
      this._initialBudgetUrlCached = url
    }
    return this._initialBudgetUrlCached
  }

  _updateSaveUrl() {
    const url = this.saveUrlsValue[String(this.activeIndexValue)] || ""
    const voiceModalEl = this.element.closest("[data-controller~='voice-modal']")
    if (voiceModalEl) {
      voiceModalEl.dataset.voiceModalSaveUrlValue = url
    }
  }

  _updateNewUrl() {
    const url = this.newUrlsValue[String(this.activeIndexValue)] || ""
    if (!url) return

    const voiceModalEl = this.element.closest("[data-controller~='voice-modal']")
    if (voiceModalEl) {
      voiceModalEl.dataset.voiceModalNewUrlValue = url
    }

    const bottomNavEl = document.querySelector("[data-controller~='bridge--bottom-nav']")
    if (bottomNavEl) {
      bottomNavEl.dataset.manualEntryUrl = url
      bottomNavEl.dataset.bridgeManualEntryUrl = url
    }

    const manualLink = document.getElementById("manual-entry-link")
    if (manualLink) {
      manualLink.setAttribute("href", url)
    }
  }

  _updateBrowserUrl() {
    const url = this.budgetUrlsValue[String(this.activeIndexValue)]
    if (url && url !== window.location.pathname) {
      const currentState = history.state || {}
      history.replaceState(currentState, "", url)
    }
  }
}
